/**
 * Returns the intersection of multiple arrays.
 *
 * This function works with arrays of any type T.
 *
 * @param arrays A list of arrays to compute the intersection of.
 *
 * @returns An array containing all elements that exist in all arrays.
 */
export function intersection<T>(...arrays: T[][]): T[] {
  if (arrays.length === 0) {
    return []
  }

  return arrays.reduce((acc, curr) => acc.filter((item) => curr.includes(item)))
}

/**
 * This function narrows the type from `T | null | undefined` to `T`.
 *
 * Use this function to filter out only `null` and `undefined` values from an array.
 *
 * @param item The item to check for definition.
 *
 * @returns true if the item is defined, false otherwise.
 *
 * @example
 * const items: (string | null | undefined)[] = ['a', null, 'b', undefined, 'c', 0, false]
 * const definedItems = items.filter(isItemDefined)
 * // => ['a', 'b', 'c', 0, false]
 */

export function isItemDefined<T>(item: T | null | undefined): item is T {
  return item !== null && item !== undefined
}

/**
 * Normalizes a single value or an array of values into an array.
 *
 * @param value A single value, an array of values, or undefined.
 *
 * @returns An array containing the value(s), or an empty array if `value` is undefined.
 *
 * @example
 * toArray('a') // => ['a']
 * toArray(['a', 'b']) // => ['a', 'b']
 * toArray(undefined) // => []
 */
export function toArray<T>(value: T[] | T | undefined | null): T[] {
  if (value === undefined || value === null) return []

  return Array.isArray(value) ? value : [value]
}
